import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Checkbox } from 'src/components/ui/checkbox';
import { Button } from 'src/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { getInitials, paletteForSeed } from 'src/lib/avatar';

// Same three roles AhedgeUsers.jsx's own filter and AddUserModal (before
// its role field was removed — see that file's comment) already used.
// Descriptions are this modal's own copy — there's no description field on
// a role anywhere in the API (roles are still hardcoded here at all; see
// this file's git history for why: /internal/reference-data's own include
// enum doesn't have a 'roles' category yet, confirmed live).
const ROLE_OPTIONS = [
  {
    value: 'ahedge_ta',
    label: 'TA',
    description:
      'Creates requisitions, screens applicants, and manages shortlists.',
  },
  {
    value: 'ahedge_cse',
    label: 'CSE',
    description: 'Owns candidate communication and interview scheduling.',
  },
  {
    value: 'ahedge_interviewer',
    label: 'Interviewer',
    description: 'Runs interview rounds and submits scorecards.',
  },
];

// A user can hold more than one role at once (RoleCell in AhedgeUsers.jsx
// already renders a "+N" overflow for exactly this), so this is a
// multi-select checkbox list, not the single-select AppSelect the old
// create-time role field used.
export const AssignRoleModal = ({
  isOpen,
  user,
  onSubmit,
  onClose,
  loading,
}) => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  // The role set as it stood when the modal opened — kept separate from
  // the live `selectedRoles` checkbox state so the "Current" badge reflects
  // what's actually saved right now, not whatever the TA has clicked so
  // far in this still-unsaved edit.
  const initialRolesRef = useRef([]);

  useEffect(() => {
    if (isOpen) {
      const current = (user?.roles || []).map((r) => r.value);
      setSelectedRoles(current);
      initialRolesRef.current = current;
    }
  }, [isOpen, user]);

  const toggleRole = (value) => {
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!user) return;
    onSubmit(user.id, selectedRoles);
  };

  const userName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(' ');
  const { bg, text } = paletteForSeed(userName || String(user?.id ?? ''));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'shrink-0 flex items-center justify-center h-11 w-11 rounded-full text-base font-semibold',
                bg,
                text
              )}
            >
              {getInitials(userName)}
            </span>
            <div className="min-w-0">
              <DialogTitle className="truncate">
                {/* Visible text is just the name, matching the design — the
                    "Assign Role" lead-in stays for screen readers only, so
                    the dialog's accessible name still says what it's for. */}
                <span className="sr-only">Assign Role — </span>
                {userName || 'This user'}
              </DialogTitle>
              <DialogDescription className="truncate">
                {user?.email}
                {user?.id != null && (
                  <>
                    {' '}
                    ·{' '}
                    <span className="font-mono font-semibold text-primary">
                      ID-{user.id}
                    </span>
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-2.5">
            <p className="text-[12.5px] text-muted-foreground">
              A user can hold more than one role.
            </p>
            {ROLE_OPTIONS.map((role) => {
              const checked = selectedRoles.includes(role.value);
              const isCurrent =
                checked && initialRolesRef.current.includes(role.value);
              return (
                <label
                  key={role.value}
                  htmlFor={`role-${role.value}`}
                  className={cn(
                    'flex flex-col gap-1 rounded-xl border px-3.5 py-3 cursor-pointer transition-colors',
                    checked
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:bg-muted/50'
                  )}
                >
                  <span className="flex items-center gap-2.5">
                    <Checkbox
                      id={`role-${role.value}`}
                      checked={checked}
                      onCheckedChange={() => toggleRole(role.value)}
                      disabled={loading}
                    />
                    <span className="text-sm font-semibold text-foreground-strong">
                      {role.label}
                    </span>
                    {isCurrent && (
                      <span className="rounded-full bg-primary/10 px-2 py-[1px] text-[11px] font-medium text-primary">
                        Current
                      </span>
                    )}
                  </span>
                  <span className="pl-[26px] text-[12.5px] text-muted-foreground">
                    {role.description}
                  </span>
                </label>
              );
            })}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="min-w-[120px]">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Roles'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
